import React, {useMemo} from 'react';

import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import DataFrameTable from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import useDataFrameTableMode from 'data-frames/table/hooks/useDataFrameTableMode';
import {useControlledDataFrameTableSelectionState} from 'data-frames/table/hooks/useDataFrameTableSelectionState';
import getMetadataModalOverflowAction from 'data-frames/toolbar/toolbar-actions/getMetadataModalOverflowAction';
import {getCopyToClipboardAction} from 'data-frames/toolbar/toolbar-actions/renderCopyToClipboardOption';
import {getDownloadAsCSVAction} from 'data-frames/toolbar/toolbar-actions/renderDownloadAsCSVOption';
import {getToggleChipsAction} from 'data-frames/toolbar/toolbar-actions/renderToggleChipsOption';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PersistedDataFrameGraphQL,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertPipelineStepRawDataIsKind, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {getOutputStepDataRawDatas} from 'pipelines/StepDataUtils';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';

import getDataFrameWarning from './data-frame-view/getDataFrameWarning';
import DataViewContext, {combineDataViewWarnings, useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';
import FailedView from './FailedView';
import LoadingView from './LoadingView';

const TABLE_MODE = DataFrameTableMode.TEXT;

interface DataFrameViewProps {
  /**
   * Optional data for analytics. Should be provided if available.
   */
  analyticsData?: {
    dataFrameId?: string;
  };
  clientId: string;
  dataFrame: DataFrame;
}

/**
 * Render a view for a `DataFrame`.
 *
 * @package
 */
export function DataFrameView({
  analyticsData: partialAnalyticsData,
  clientId,
  dataFrame,
}: DataFrameViewProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {
    actionInput,
    actionOrigin,
    dataFrameViewOptions: {dataFrameRenderOverrides, headerMenuActions},
    rowSelectionState: {selectedRowIndices, setSelectedRowIndices},
  } = dataViewContextValue;

  const analyticsData = useMemo(
    () => ({
      ...partialAnalyticsData,
      actionOrigin,
    }),
    [actionOrigin, partialAnalyticsData]
  );

  const {tableMode, setTableMode} = useDataFrameTableMode(clientId, TABLE_MODE);

  const dataFrameTableSortOptions = useSimpleTableSortState(clientId);
  const sortedDataFrame = useMemo(
    () => sortDataFrame(dataFrame, dataFrameTableSortOptions.sortState, TABLE_MODE),
    [dataFrame, dataFrameTableSortOptions.sortState]
  );

  const selectionStateHookOutput = useControlledDataFrameTableSelectionState(
    sortedDataFrame,
    new Set([...selectedRowIndices].map((index) => String(index))),
    (selectedRowIds: Set<string>) => {
      setSelectedRowIndices(new Set([...selectedRowIds].map((id) => Number(id))));
    }
  );

  const newDataFrameViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [
          ...dataViewContextValue.actions.overflowActions,
          getCopyToClipboardAction(sortedDataFrame, analyticsData, selectionStateHookOutput.selectedRowIds),
          getDownloadAsCSVAction(sortedDataFrame, analyticsData, selectionStateHookOutput.selectedRowIds),
          getToggleChipsAction(analyticsData, tableMode, setTableMode),
        ],
      },
    }),
    [analyticsData, dataViewContextValue, selectionStateHookOutput, setTableMode, sortedDataFrame, tableMode]
  );

  return (
    <DataViewContext.Provider value={newDataFrameViewContextValue}>
      <DataViewPane>
        <DataFrameTable
          actionOrigin={actionOrigin}
          dataFrame={sortedDataFrame}
          clientStateId={clientId}
          dataFrameRenderOverrides={dataFrameRenderOverrides}
          headerMenuOptions={headerMenuActions?.(actionInput) ?? undefined}
          mode={tableMode}
          renderIsExcludedColumn={false}
          selectionOptions={{allowRowSelection: true, ...selectionStateHookOutput}}
          sortOptions={dataFrameTableSortOptions}
        />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

interface PersistedDataFrameViewProps {
  persistedDataFrame: PersistedDataFrameGraphQL;
}

/**
 * Render view for a `PersistedDataFrame`.
 *
 * Delegates rendering the `dataFrameJson` table content to `DataFrameView`.
 *
 * @package
 */
function PersistedDataFrameView({persistedDataFrame}: PersistedDataFrameViewProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  const newDataViewContextValue = useMemo(() => {
    const {
      actions: {overflowActions, ...remainingActions},
      additionalMetadataContent,
    } = dataViewContextValue;

    const {allColumnsJson, dataFrameJson} = persistedDataFrame;

    return {
      ...dataViewContextValue,
      actions: {
        ...remainingActions,
        overflowActions: [
          ...overflowActions,
          getMetadataModalOverflowAction(persistedDataFrame, additionalMetadataContent),
        ],
      },
      actionInput: {persistedDataFrame},
      warning: combineDataViewWarnings(
        dataViewContextValue.warning,
        getDataFrameWarning({
          renderedColumns: dataFrameJson.columns.length,
          renderedRows: dataFrameJson.rows.length,
          totalColumns: allColumnsJson.length,
          totalRows: dataFrameJson.numRows ?? dataFrameJson.rows.length,
        })
      ),
    };
  }, [persistedDataFrame, dataViewContextValue]);

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataFrameView
        analyticsData={{dataFrameId: persistedDataFrame.id}}
        clientId={persistedDataFrame.id}
        dataFrame={persistedDataFrame.dataFrameJson}
      />
    </DataViewContext.Provider>
  );
}

/**
 * Render view that queries a `PersistedDataFrame`.
 *
 * Delegates rendering the queried contents to `PersistedDataFrameView`.
 *
 * @package
 */
export default function QueryingPersistedDataFrameView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, previewData} = dataViewContextValue;

  const stepOutputReference = analysisView.lineage[analysisView.lineage.length - 1].stepOutputReference!;

  const dataFrame =
    previewData && pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, previewData.draftStep)
      ? getOutputStepDataRawDatas<PipelineStepType.TABLE_VIEW, 0>(
          previewData.draftStep,
          SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
        )[0]
      : stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

  assertPipelineStepRawDataIsKind(dataFrame, PipelineStepDataKind.DATASET);

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{dataFrameId: dataFrame.id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame: persistedDataFrame}) =>
        // `assertAndContinue` can't narrow types like `assert` does so check the condition twice.
        assertAndContinue(
          !isInaccessible(persistedDataFrame),
          'Expected users to have access to all tables in analyses.'
        ) && !isInaccessible(persistedDataFrame) ? (
          <PersistedDataFrameView persistedDataFrame={persistedDataFrame} />
        ) : (
          <FailedView />
        )
      }
    </BenchlingQuery>
  );
}
